import {
  RECORD_TYPE,
  FIELD,
  VALIDATION_RULE,
  APPROVAL_PROCESS,
  WORKFLOW_RULE,
  PROCESS_BUILDER_FLOW,
  FLOW,
  APEX_TRIGGER,
  APEX_CLASS,
  LAYOUT,
  ObjectParsedDataByType,
} from '@server/parser';
import { BaseFieldProperties } from '@server/documentation';
import {
  SWEEP_AUTOMATION,
  SWEEP_ALERT,
  SWEEP_ASSIGNMENT,
  SWEEP_SCHEDULED_ASSIGNMENT,
  SWEEP_DEDUPE,
  SWEEP_MATCHING,
  ROLLUP,
} from '../../components/parser/ConfigurationItemTypes';
import { GlobalDto } from '../global/globalReducerTypes';

export interface DocumentationItemsMap {
  [RECORD_TYPE]: string[];
  [FIELD]: string[];
  [VALIDATION_RULE]: string[];
  [APPROVAL_PROCESS]: string[];
  [WORKFLOW_RULE]: string[];
  [PROCESS_BUILDER_FLOW]: string[];
  [FLOW]: string[];
  [APEX_TRIGGER]: string[];
  [APEX_CLASS]: string[];
  [LAYOUT]: string[];
  [SWEEP_AUTOMATION]: string[];
  [SWEEP_ALERT]: string[];
  [SWEEP_ASSIGNMENT]: string[];
  [SWEEP_SCHEDULED_ASSIGNMENT]: string[];
  [SWEEP_DEDUPE]: string[];
  [SWEEP_MATCHING]: string[];
}

// Generic type for objects that have an id property
interface Identifiable {
  id?: string;
  isActive?: boolean;
}

// Configuration types that are part of globalDto.documentation
const DOCUMENTATION_CONFIGURATION_MAPPING = {
  RECORD_TYPE,
  FIELD,
  VALIDATION_RULE,
  APPROVAL_PROCESS,
  WORKFLOW_RULE,
  PROCESS_BUILDER_FLOW,
  FLOW,
  APEX_TRIGGER,
  APEX_CLASS,
  LAYOUT,
} as const;

// Configuration types that are direct properties of globalDto
const ROOT_LEVEL_CONFIGURATION_MAPPING = {
  automations: { targetKey: SWEEP_AUTOMATION, idProperty: 'automationId' },
  alerts: { targetKey: SWEEP_ALERT, idProperty: 'automationId' },
  assignments: { targetKey: SWEEP_ASSIGNMENT, idProperty: 'automationId' },
  scheduledAssignments: {
    targetKey: SWEEP_SCHEDULED_ASSIGNMENT,
    idProperty: 'automationId',
  },
  dedupe: { targetKey: SWEEP_DEDUPE, idProperty: 'automationId' },
  matching: { targetKey: SWEEP_MATCHING, idProperty: 'automationId' },
  rollups: { targetKey: ROLLUP, idProperty: 'rollupId' }, // Rollups are assigned to fields category
} as const;

/**
 * Extracts IDs from an array of objects that have an optional id property and are not explicitly inactive
 */
const extractIds = <T extends Identifiable>(items: T[] | undefined): string[] => {
  if (!items?.length) return [];
  return items.filter((item) => item.id && item.isActive !== false).map((item) => item.id!);
};

/**
 * Extracts IDs from sweep elements using the correct ID property and are not explicitly inactive
 */
const extractSweepElementIds = (items: any[] | undefined, idProperty: string): string[] => {
  if (!items?.length) return [];
  return items
    .filter((item) => item[idProperty] && item.isActive !== false)
    .map((item) => item[idProperty]);
};

/**
 * Extracts IDs from recordTypes using object.name and recordType.name
 */
const extractRecordTypeIds = (objectData: any): string[] => {
  if (!objectData.recordTypes?.length || !objectData.name) return [];
  return objectData.recordTypes
    .filter((recordType: any) => recordType.name && recordType.isActive !== false)
    .map((recordType: any) => `${objectData.name}.${recordType.name}`);
};

/**
 * Processes a single object data item and extracts IDs by configuration type
 */
const processDocumentationObjectData = (
  objectData: ObjectParsedDataByType,
): Partial<DocumentationItemsMap> => {
  const result: Partial<DocumentationItemsMap> = {};

  // Special handling for recordTypes
  const recordTypeIds = extractRecordTypeIds(objectData);
  if (recordTypeIds.length > 0) {
    result[RECORD_TYPE] = recordTypeIds;
  }

  // Process other configuration types from documentation objects (excluding recordTypes)
  Object.entries(DOCUMENTATION_CONFIGURATION_MAPPING).forEach(([, targetKey]) => {
    if (targetKey === RECORD_TYPE) return; // Skip recordTypes as it's handled above

    const data = Array.isArray(objectData.objectElements[targetKey])
      ? objectData.objectElements[targetKey]
      : [];

    const ids = extractIds(data as BaseFieldProperties[]);
    if (ids.length > 0) {
      result[targetKey as keyof DocumentationItemsMap] = ids;
    }
  });

  return result;
};

/**
 * Processes root-level configuration types from globalDto
 */
const processRootLevelConfiguration = (globalDto: GlobalDto): Partial<DocumentationItemsMap> => {
  const result: Partial<DocumentationItemsMap> = {};

  // Process configuration types that are direct properties of globalDto
  Object.entries(ROOT_LEVEL_CONFIGURATION_MAPPING).forEach(([sourceKey, config]) => {
    const { targetKey, idProperty } = config;
    const ids = extractSweepElementIds((globalDto as any)[sourceKey], idProperty);
    if (ids.length > 0) {
      if (!result[targetKey as keyof DocumentationItemsMap]) {
        result[targetKey as keyof DocumentationItemsMap] = [];
      }
      (result[targetKey as keyof DocumentationItemsMap] as string[]).push(...ids);
    }
  });

  return result;
};

/**
 * Merges partial results into the main items map
 */
const mergeResults = (
  target: DocumentationItemsMap,
  source: Partial<DocumentationItemsMap>,
): void => {
  Object.entries(source).forEach(([key, ids]) => {
    if (ids && Array.isArray(ids)) {
      (target as any)[key]?.push(...ids);
    }
  });
};

/**
 * Creates an empty items map with all required properties
 * It doesn't include rollups as they are in fields category
 */
const createEmptyItemsMap = (): DocumentationItemsMap => ({
  [RECORD_TYPE]: [],
  [FIELD]: [],
  [VALIDATION_RULE]: [],
  [APPROVAL_PROCESS]: [],
  [WORKFLOW_RULE]: [],
  [PROCESS_BUILDER_FLOW]: [],
  [FLOW]: [],
  [APEX_TRIGGER]: [],
  [APEX_CLASS]: [],
  [LAYOUT]: [],
  [SWEEP_AUTOMATION]: [],
  [SWEEP_ALERT]: [],
  [SWEEP_ASSIGNMENT]: [],
  [SWEEP_SCHEDULED_ASSIGNMENT]: [],
  [SWEEP_DEDUPE]: [],
  [SWEEP_MATCHING]: [],
});

/**
 * Creates a flat map of item IDs from GlobalDto, organized by configuration type.
 *
 * @param globalDto - The GlobalDto containing documentation and configuration data
 * @returns DocumentationItemsMap with arrays of IDs for each configuration type
 */
export const createDocumentationItemsMap = (globalDto: GlobalDto): DocumentationItemsMap => {
  const itemsMap = createEmptyItemsMap();

  // Process root-level configuration types (sweep elements and rollups)
  const rootLevelResults = processRootLevelConfiguration(globalDto);
  mergeResults(itemsMap, rootLevelResults);

  // Process documentation array for standard configuration types
  if (
    globalDto.documentation.parsedObjects &&
    Array.isArray(globalDto.documentation.parsedObjects)
  ) {
    globalDto.documentation.parsedObjects.forEach((objectData) => {
      const documentationResults = processDocumentationObjectData(objectData);
      mergeResults(itemsMap, documentationResults);
    });
  }

  return itemsMap;
};
